import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';

type FeedResult = { path: string };

const CalendarProviderIcons: React.FC = () => (
  <span className="td-calendar-provider-icons" aria-hidden="true">
    <svg className="td-provider-icon" viewBox="0 0 24 24">
      <rect x="2" y="2" width="20" height="20" rx="4" fill="#fff" />
      <path d="M2 8h20v10a4 4 0 0 1-4 4H8l-6-6z" fill="#4285f4" />
      <path d="M2 8V6a4 4 0 0 1 4-4h5v6z" fill="#34a853" />
      <path d="M11 2h7a4 4 0 0 1 4 4v2H11z" fill="#ea4335" />
      <path d="M2 16V8h6z" fill="#fbbc04" />
      <text x="12" y="17.2" textAnchor="middle" fontSize="8" fontWeight="800" fill="#fff">31</text>
    </svg>
    <svg className="td-provider-icon" viewBox="0 0 24 24">
      <rect x="5" y="3" width="17" height="18" rx="3" fill="#1473e6" />
      <path d="M5 8h17M10 3v18" stroke="#fff" strokeOpacity=".62" strokeWidth="1.2" />
      <rect x="1" y="6" width="12" height="13" rx="2" fill="#075da8" />
      <text x="7" y="15.5" textAnchor="middle" fontSize="8.5" fontWeight="800" fill="#fff">O</text>
    </svg>
    <svg className="td-provider-icon td-provider-icon--apple" viewBox="0 0 24 24">
      <rect x="2" y="2" width="20" height="20" rx="4" fill="#fff" stroke="#cbd5e1" />
      <path d="M2 6a4 4 0 0 1 4-4h12a4 4 0 0 1 4 4v3H2z" fill="#ef4444" />
      <text x="12" y="18" textAnchor="middle" fontSize="8" fontWeight="800" fill="#111827">31</text>
    </svg>
  </span>
);

const TeacherCalendarSubscription: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [feedUrl, setFeedUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const createLink = async () => {
    if (feedUrl && !await window.appConfirm('Replace your current calendar link? Calendars using the old link will stop updating.')) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const callable = httpsCallable<Record<string, never>, FeedResult>(getFunctions(), 'createTeacherCalendarFeed');
      const result = await callable({});
      const url = new URL(result.data.path, window.location.origin).toString();
      setFeedUrl(url);
      setMessage('Private subscription link created. Add it to your calendar before closing this window.');
    } catch (nextError: any) {
      setError(nextError?.message || 'Could not create the calendar link.');
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(feedUrl);
      setMessage('Calendar link copied.');
    } catch {
      setError('Copy failed. Select the link and copy it manually.');
    }
  };

  const openAppleCalendar = () => {
    if (!feedUrl) return;
    window.location.href = feedUrl.replace(/^https?:\/\//, 'webcal://');
  };

  const revoke = async () => {
    if (!await window.appConfirm('Revoke this calendar feed? Any subscribed calendars will stop receiving updates.')) return;
    setBusy(true);
    setError('');
    try {
      const callable = httpsCallable(getFunctions(), 'revokeTeacherCalendarFeed');
      await callable({});
      setFeedUrl('');
      setMessage('Calendar feed revoked.');
    } catch (nextError: any) {
      setError(nextError?.message || 'Could not revoke the calendar feed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button type="button" className="td-link-btn" onClick={() => setOpen(true)}>
        <CalendarProviderIcons />
        <span>Subscribe to calendar</span>
      </button>
      {open && (
        <div className="td-calendar-subscribe-backdrop" role="presentation" onMouseDown={() => setOpen(false)}>
          <section
            className="td-calendar-subscribe"
            role="dialog"
            aria-modal="true"
            aria-labelledby="teacher-calendar-title"
            onMouseDown={event => event.stopPropagation()}
          >
            <button type="button" className="td-calendar-subscribe-close" onClick={() => setOpen(false)} aria-label="Close">×</button>
            <h2 id="teacher-calendar-title">Subscribe to your Overture schedule</h2>
            <p>
              Add one private link to Apple Calendar, Google Calendar, or Outlook. Your calendar provider will periodically
              check it for schedule, location, and substitute changes.
            </p>

            {!feedUrl ? (
              <div className="td-calendar-subscribe-callout">
                <strong>Create a new private link</strong>
                <span>Creating a link replaces any link you generated previously. Treat it like a password.</span>
                <button type="button" className="td-btn td-btn-launch" disabled={busy} onClick={createLink}>
                  {busy ? 'Creating…' : 'Create / replace link'}
                </button>
              </div>
            ) : (
              <>
                <label className="td-calendar-subscribe-label" htmlFor="teacher-calendar-url">Private calendar URL</label>
                <div className="td-calendar-subscribe-url-row">
                  <input id="teacher-calendar-url" value={feedUrl} readOnly onFocus={event => event.currentTarget.select()} />
                  <button type="button" className="td-btn td-btn-launch" onClick={copyLink}>Copy</button>
                </div>
                <div className="td-calendar-subscribe-actions">
                  <button type="button" className="td-link-btn" onClick={openAppleCalendar}>Open in Apple Calendar</button>
                  <a className="td-link-btn" href="https://calendar.google.com/calendar/u/0/r/settings/addbyurl" target="_blank" rel="noreferrer">
                    Open Google Calendar settings
                  </a>
                  <a className="td-link-btn" href="https://outlook.live.com/calendar/0/addcalendar" target="_blank" rel="noreferrer">
                    Open Outlook
                  </a>
                </div>
                <p className="td-calendar-subscribe-help">
                  For Google Calendar choose <strong>From URL</strong>. For Outlook choose <strong>Subscribe from web</strong>, then paste the copied link.
                </p>
                <button type="button" className="td-calendar-subscribe-revoke" disabled={busy} onClick={revoke}>Revoke feed</button>
              </>
            )}
            {message && <div className="td-calendar-subscribe-message" role="status">{message}</div>}
            {error && <div className="td-calendar-subscribe-error" role="alert">{error}</div>}
          </section>
        </div>
      )}
    </>
  );
};

export default TeacherCalendarSubscription;
